// /ip-bans — active ban list + add/remove IP bans (Wave 4)
// Spec: docs/plans/admin-tooling.md D-62

import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { apiFetch } from '@/lib/api';
import { Button, Input, Field, ErrorChip, PageHeader } from '@awakenedlands/ui';
import { StepUpModal } from '@/components/admin/step-up-modal';

// ── Types ─────────────────────────────────────────────────────────────────────

interface IpBanRow {
  ip: string;
  banned_at: number;
  expires_at: number | null;
  reason: string;
  banned_by_staff_id: number | null;
}

interface IpBanListResponse {
  rows: IpBanRow[];
  next_cursor: string | null;
}

// ── ModalShell ────────────────────────────────────────────────────────────────

function ModalShell({ children }: { children: React.ReactNode }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-background/85">
      <div className="relative w-full max-w-sm mx-4 rounded-sm border border-border bg-panel-elev p-5 shadow-[0_24px_48px_rgba(0,0,0,0.4)] space-y-4">
        {children}
      </div>
    </div>
  );
}

// ── BlastRadiusCaution ────────────────────────────────────────────────────────
// Staff-facing warning at the point of action (#815).
//
// Since #801 an IP ban is a GLOBAL block: it removes the entire game from every
// account behind that address, not the 16 routes it degraded before. That is
// legacy-faithful (`header.php:126` did the same), so this is not a parity
// complaint — the gap is that nothing told staff. CGNAT, mobile carriers,
// schools, offices and shared households routinely put many unrelated players
// behind one address, so the collateral set is large and invisible from here.

function BlastRadiusCaution() {
  return (
    <div
      className="rounded-sm border border-[--color-status-warn]/40 bg-[--color-status-warn]/10 px-3 py-2 space-y-1"
      data-testid="ip-ban-blast-radius-caution"
    >
      <p className="text-xs font-semibold uppercase tracking-wider text-[--color-status-warn]">
        Blocks the whole game for everyone on this address
      </p>
      <p className="text-xs text-ink-mid">
        Every account behind this IP loses access to all of Awakened Lands, not just the
        player you have in mind. Shared connections — CGNAT, mobile carriers, schools,
        offices, households — are common and you cannot see who else is affected from
        this screen.
      </p>
      <p className="text-xs text-ink-mid">
        Prefer an account-level action first: <span className="text-ink-high">lock</span>{' '}
        or <span className="text-ink-high">fedjail</span> the specific player. Use an IP
        ban when the abuse is not tied to one account.
      </p>
    </div>
  );
}

// ── IpBanModal (self-contained) ───────────────────────────────────────────────
// State machine: form → stepup → submitting → done | error

type ModalMode = 'add' | 'unban';
type ModalPhase = 'form' | 'stepup' | 'submitting' | 'done' | 'error';

interface IpBanModalProps {
  mode: ModalMode;
  targetIp?: string | undefined; // required when mode='unban'
  onDone: () => void;
  onCancel: () => void;
}

function IpBanModal({ mode, targetIp, onDone, onCancel }: IpBanModalProps) {
  const [phase, setPhase] = useState<ModalPhase>('form');
  const [ip, setIp] = useState(targetIp ?? '');
  const [reason, setReason] = useState('');
  const [expiresAt, setExpiresAt] = useState(''); // date string from <input type="date">
  const [stepupToken, setStepupToken] = useState('');
  const [errorMsg, setErrorMsg] = useState('');

  function handleFormSubmit() {
    if (mode === 'add' && !ip.trim()) return;
    if (!reason.trim()) return;
    setPhase('stepup');
  }

  function handleVerified(token: string) {
    setStepupToken(token);
    setPhase('submitting');

    void (async () => {
      let res;
      if (mode === 'add') {
        const body: Record<string, unknown> = { ip: ip.trim(), reason: reason.trim() };
        if (expiresAt) {
          body['expires_at'] = Math.floor(new Date(expiresAt).getTime() / 1000);
        }
        res = await apiFetch('/admin/ip-ban', {
          method: 'POST',
          headers: { 'X-Stepup-Token': token },
          body: JSON.stringify(body),
        });
      } else {
        res = await apiFetch(`/admin/ip-ban/${encodeURIComponent(targetIp ?? '')}/clear`, {
          method: 'POST',
          headers: { 'X-Stepup-Token': token },
          body: JSON.stringify({ reason: reason.trim() }),
        });
      }

      if (!res.ok) {
        const data = res.data as Record<string, unknown>;
        setErrorMsg((data['error'] as string | undefined) ?? 'Action failed. Please try again.');
        setPhase('error');
        return;
      }

      setPhase('done');
      setTimeout(() => { onDone(); }, 600);
    })();
  }

  function handleCancelStepup() {
    setPhase('form');
  }

  if (phase === 'stepup') {
    return (
      <StepUpModal
        actionIntent={mode === 'add' ? 'ip-ban' : 'ip-unban'}
        targetUserId={null}
        onVerified={handleVerified}
        onCancel={handleCancelStepup}
      />
    );
  }

  // stepupToken consumed — suppress unused-var lint
  void stepupToken;

  return (
    <ModalShell>
      <h2 className="text-base font-semibold text-ink-high">
        {mode === 'add' ? 'Add IP Ban' : `Unban ${targetIp ?? ''}`}
      </h2>

      {(phase === 'form' || phase === 'submitting') && (
        <div className="space-y-4">
          {mode === 'add' && <BlastRadiusCaution />}

          {mode === 'add' && (
            <>
              {/* Field renders a single child and reads its props.id — keep the
                  hint as a sibling, matching the expiry hint below. */}
              <Field label="IP Address">
                <Input
                  id="ban-ip"
                  type="text"
                  placeholder="e.g. 1.2.3.4"
                  value={ip}
                  onChange={(e) => setIp(e.target.value)}
                  disabled={phase === 'submitting'}
                />
              </Field>
              <p className="text-xs text-ink-low">
                Single address only — ranges (<span className="font-mono">1.2.3.0/24</span>)
                are rejected. IPv6 bans one exact address; a client with a /64 can rotate
                out of it.
              </p>
            </>
          )}

          <div className="space-y-1">
            <label className="block text-xs font-medium uppercase tracking-wider text-ink-mid" htmlFor="ban-reason">
              Reason
            </label>
            <textarea
              id="ban-reason"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={3}
              placeholder="Required"
              disabled={phase === 'submitting'}
              className="w-full rounded-sm border border-border bg-panel px-3 py-2 text-sm text-ink-high focus:outline-none focus:ring-1 focus:ring-primary"
            />
          </div>

          {mode === 'add' && (
            <Field label="Expires (optional)">
              <Input
                id="ban-expires"
                type="date"
                value={expiresAt}
                onChange={(e) => setExpiresAt(e.target.value)}
                disabled={phase === 'submitting'}
              />
            </Field>
          )}
          {mode === 'add' && (
            <p className="text-xs text-ink-low">Leave blank for permanent ban.</p>
          )}

          <div className="flex items-center gap-3">
            <Button
              type="button"
              onClick={handleFormSubmit}
              disabled={phase === 'submitting' || !reason.trim() || (mode === 'add' && !ip.trim())}
              width="full"
            >
              {phase === 'submitting' ? 'Submitting…' : mode === 'add' ? 'Add Ban' : 'Unban'}
            </Button>
            <button
              type="button"
              onClick={onCancel}
              disabled={phase === 'submitting'}
              className="text-sm text-ink-low hover:text-ink-mid underline"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {phase === 'done' && (
        <p className="text-sm text-[--color-status-ok] text-center py-4">Done.</p>
      )}

      {phase === 'error' && (
        <div className="space-y-3">
          <ErrorChip>{errorMsg}</ErrorChip>
          <div className="flex items-center gap-3">
            <Button type="button" onClick={() => { setPhase('form'); }} width="full">
              Try again
            </Button>
            <button
              type="button"
              onClick={onCancel}
              className="text-sm text-ink-low hover:text-ink-mid underline"
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </ModalShell>
  );
}

// ── IpBansPage ────────────────────────────────────────────────────────────────

export function IpBansPage() {
  const queryClient = useQueryClient();
  const [modal, setModal] = useState<{ mode: ModalMode; ip?: string } | null>(null);

  const { data, isLoading, isError } = useQuery<IpBanListResponse>({
    queryKey: ['ip-bans'],
    queryFn: async () => {
      const res = await apiFetch<IpBanListResponse>('/admin/ip-ban?limit=50');
      if (!res.ok) throw new Error('Failed to load IP bans.');
      return res.data;
    },
  });

  function handleDone() {
    setModal(null);
    void queryClient.invalidateQueries({ queryKey: ['ip-bans'] });
  }

  const rows = data?.rows ?? [];

  return (
    <div className="space-y-6">
      <PageHeader
        title="IP Bans"
        description="Active IP bans. Each one blocks the whole game for every account on that address — prefer account-level lock or fedjail first. Step-up required to add or remove."
        actions={
          <Button
            type="button"
            onClick={() => setModal({ mode: 'add' })}
            data-testid="add-ip-ban-btn"
          >
            Add IP Ban
          </Button>
        }
      />

      {/* Loading */}
      {isLoading && (
        <div className="flex justify-center py-8">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-ink-low border-t-primary" />
        </div>
      )}

      {/* Error */}
      {isError && (
        <ErrorChip>Failed to load IP bans.</ErrorChip>
      )}

      {/* Empty */}
      {!isLoading && !isError && rows.length === 0 && (
        <p className="text-sm text-ink-low">No active IP bans.</p>
      )}

      {/* Table */}
      {!isLoading && !isError && rows.length > 0 && (
        <div className="overflow-x-auto rounded-sm border border-border">
          <table className="w-full text-sm" data-testid="ip-bans-table">
            <thead className="bg-muted text-left">
              <tr>
                <th className="px-3 py-2 font-medium text-ink-mid">IP</th>
                <th className="px-3 py-2 font-medium text-ink-mid">Banned at</th>
                <th className="px-3 py-2 font-medium text-ink-mid">Expires</th>
                <th className="px-3 py-2 font-medium text-ink-mid">Reason</th>
                <th className="px-3 py-2 font-medium text-ink-mid">Staff ID</th>
                <th className="px-3 py-2 font-medium text-ink-mid" />
              </tr>
            </thead>
            <tbody className="divide-y divide-divider">
              {rows.map((row) => (
                <tr key={row.ip} className="hover:bg-panel-elev" data-testid={`ban-row-${row.ip}`}>
                  <td className="px-3 py-2 font-mono text-xs tabular-nums">{row.ip}</td>
                  <td className="px-3 py-2 text-ink-low whitespace-nowrap font-mono tabular-nums text-xs">
                    {new Date(row.banned_at * 1000).toLocaleString()}
                  </td>
                  <td className="px-3 py-2 text-ink-low whitespace-nowrap font-mono tabular-nums text-xs">
                    {row.expires_at !== null
                      ? new Date(row.expires_at * 1000).toLocaleString()
                      : 'Permanent'}
                  </td>
                  <td className="px-3 py-2 text-ink-mid">{row.reason}</td>
                  <td className="px-3 py-2 text-ink-low tabular-nums">
                    {row.banned_by_staff_id !== null ? row.banned_by_staff_id : '—'}
                  </td>
                  <td className="px-3 py-2 text-right">
                    <Button
                      type="button"
                      variant="secondary"
                      onClick={() => setModal({ mode: 'unban', ip: row.ip })}
                      data-testid={`unban-btn-${row.ip}`}
                    >
                      Unban
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Modal */}
      {modal && (
        <IpBanModal
          mode={modal.mode}
          targetIp={modal.ip}
          onDone={handleDone}
          onCancel={() => setModal(null)}
        />
      )}
    </div>
  );
}
